(function () {
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  const REVISION = '2026-01-15';

  function getConfig() {
    return window.themeVariables && window.themeVariables.settings
      ? window.themeVariables.settings
      : {};
  }

  function getStrings() {
    return window.themeVariables?.strings?.waitlist || {};
  }

  function stripTrademarkSymbols(str) {
    if (!str) return '';
    return String(str)
      .replace(/\u2122/g, '')
      .replace(/\u00AE/gi, '')
      .replace(/\(\s*TM\s*\)/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Normalize to E.164 for Klaviyo (no spaces). AU locals (04…) → +61…
   * Returns '' if empty; null if non-empty but invalid.
   */
  function toE164Phone(raw) {
    if (raw == null) return '';
    const trimmed = String(raw).trim();
    if (!trimmed) return '';

    let digits = trimmed.replace(/[^\d+]/g, '');
    if (digits.startsWith('+')) {
      digits = '+' + digits.slice(1).replace(/\D/g, '');
    } else {
      digits = digits.replace(/\D/g, '');
      if (digits.startsWith('0') && digits.length === 10) {
        digits = '+61' + digits.slice(1);
      } else if (digits.startsWith('61') && digits.length >= 11) {
        digits = '+' + digits;
      } else if (digits.length >= 10) {
        digits = '+' + digits;
      } else {
        return null;
      }
    }

    if (!/^\+[1-9]\d{7,14}$/.test(digits)) return null;
    return digits;
  }

  /**
   * @param {object} opts
   * @param {string} opts.email
   * @param {string} [opts.firstName]
   * @param {string} [opts.phoneE164] — only with SMS consent
   * @param {string} opts.listId
   * @param {string} [opts.productTitle]
   * @param {boolean} [opts.appendWaitlistProduct=true] — false on step 2 to avoid duplicate appends
   * @param {boolean} [opts.includeEmailConsent=true]
   */
  function buildPayload(opts) {
    const email = opts.email;
    const firstName = opts.firstName;
    const phoneE164 = opts.phoneE164;
    const listId = opts.listId;
    const productTitle = opts.productTitle;
    const appendWaitlistProduct = opts.appendWaitlistProduct !== false;
    const includeEmailConsent = opts.includeEmailConsent !== false;

    const profileAttrs = {
      email: email.trim(),
      subscriptions: {},
    };

    if (includeEmailConsent) {
      profileAttrs.subscriptions.email = {
        marketing: {
          consent: 'SUBSCRIBED',
        },
      };
    }

    const fn = firstName && firstName.trim();
    if (fn) profileAttrs.first_name = fn;

    // Never send phone without SMS marketing consent — otherwise Klaviyo stores
    // the number as Never Subscribed (profile property only).
    if (phoneE164) {
      profileAttrs.phone_number = phoneE164;
      profileAttrs.subscriptions.sms = {
        marketing: {
          consent: 'SUBSCRIBED',
        },
      };
    }

    const title = stripTrademarkSymbols(productTitle);
    if (appendWaitlistProduct && title) {
      profileAttrs.meta = {
        patch_properties: {
          append: {
            waitlist_products: title,
          },
        },
      };
    }

    const customSource = title
      ? 'Product card waitlist: ' + title
      : 'Product card waitlist';

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttrs,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };
  }

  function setVisible(el, show) {
    if (!el) return;
    el.hidden = !show;
    if (!show) el.textContent = '';
  }

  function getStep(modal, step) {
    return modal?.querySelector('.waitlist-modal__step[data-step="' + step + '"]');
  }

  function getStepError(modal, step) {
    return modal?.querySelector('.waitlist-modal__message--step' + step);
  }

  function showStep(modal, step) {
    if (!modal) return;
    const step1 = getStep(modal, 1);
    const step2 = getStep(modal, 2);
    if (step1) step1.hidden = step !== 1;
    if (step2) step2.hidden = step !== 2;
    modal.dataset.waitlistStep = String(step);

    setVisible(getStepError(modal, 1), false);
    setVisible(getStepError(modal, 2), false);

    if (step === 2) {
      const phoneInput = document.getElementById('waitlist-modal-phone');
      phoneInput?.focus();
    }
  }

  function resetWaitlistModalFormState(modal) {
    if (!modal) return;
    const formBody = modal.querySelector('.waitlist-modal__form-body');
    const successPanel = modal.querySelector('.waitlist-modal__success-panel');
    const okEl = modal.querySelector('.waitlist-modal__success');
    if (formBody) formBody.hidden = false;
    if (successPanel) successPanel.hidden = true;
    if (okEl) okEl.textContent = '';
    showStep(modal, 1);
    delete modal.dataset.waitlistEmail;
    delete modal.dataset.waitlistFirstName;
  }

  function showWaitlistSuccessState(modal, message) {
    if (!modal) return;
    const formBody = modal.querySelector('.waitlist-modal__form-body');
    const successPanel = modal.querySelector('.waitlist-modal__success-panel');
    const okEl = modal.querySelector('.waitlist-modal__success');
    const productEl = modal.querySelector('.waitlist-modal__product');
    if (formBody) formBody.hidden = true;
    if (productEl) productEl.hidden = true;
    if (okEl) okEl.textContent = message;
    if (successPanel) successPanel.hidden = false;
  }

  function parseKlaviyoErrors(body) {
    if (!body || !Array.isArray(body.errors)) return '';
    return body.errors
      .map(function (e) {
        return e.detail || e.title || '';
      })
      .filter(Boolean)
      .join(' ');
  }

  async function subscribeToKlaviyo(payload, companyId) {
    const res = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: REVISION,
      },
      body: JSON.stringify(payload),
    });

    const resText = await res.text();
    let resJson = null;
    try {
      resJson = resText ? JSON.parse(resText) : null;
    } catch (_) {}

    const partialFailure = (res.headers.get('X-Klaviyo-Partial-Failure') || '').toLowerCase();

    return { res, resText, resJson, partialFailure };
  }

  function syncSmsConsentRequired() {
    const phoneInput = document.getElementById('waitlist-modal-phone');
    const smsConsentInput = document.getElementById('waitlist-modal-sms-consent');
    if (!phoneInput || !smsConsentInput) return;
    smsConsentInput.required = !!phoneInput.value.trim();
  }

  function getConfigErrors(errEl, strings) {
    const companyId = getConfig().klaviyoPublicApiKey;
    const listId = getConfig().klaviyoWaitlistListId;

    if (!companyId) {
      if (errEl) {
        errEl.textContent = strings.errorConfig || 'Signup is not configured yet.';
        errEl.hidden = false;
      }
      return null;
    }

    if (!listId) {
      if (errEl) {
        errEl.textContent =
          strings.errorListConfig ||
          strings.errorGeneric ||
          'Something went wrong. Please try again.';
        errEl.hidden = false;
      }
      return null;
    }

    return { companyId, listId };
  }

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('button.product-card__waitlist-button');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const modal = document.getElementById('waitlist-modal');
    if (!modal || typeof modal.show !== 'function') return;

    const title = stripTrademarkSymbols(btn.getAttribute('data-waitlist-product-title') || '');

    const productEl = modal.querySelector('.waitlist-modal__product');
    const form = document.getElementById('waitlist-modal-form');

    resetWaitlistModalFormState(modal);
    if (form) form.reset();
    syncSmsConsentRequired();

    if (productEl) {
      if (title) {
        productEl.textContent = title;
        productEl.hidden = false;
      } else {
        productEl.textContent = '';
        productEl.hidden = true;
      }
    }

    modal.dataset.waitlistProductTitle = title || '';
    modal.show();
  });

  const phoneInputEl = document.getElementById('waitlist-modal-phone');
  phoneInputEl?.addEventListener('input', syncSmsConsentRequired);
  phoneInputEl?.addEventListener('change', syncSmsConsentRequired);
  phoneInputEl?.addEventListener('blur', syncSmsConsentRequired);

  document.getElementById('waitlist-modal')?.addEventListener('click', function (e) {
    const skipBtn = e.target.closest('[data-waitlist-action="skip"]');
    if (!skipBtn) return;

    e.preventDefault();
    const modal = document.getElementById('waitlist-modal');
    const strings = getStrings();
    showWaitlistSuccessState(modal, strings.success || "You're on the list.");
  });

  document.getElementById('waitlist-modal-form')?.addEventListener('submit', async function (e) {
    e.preventDefault();

    const formEl = e.target;
    if (!(formEl instanceof HTMLFormElement)) return;

    const modal = document.getElementById('waitlist-modal');
    const strings = getStrings();
    const step = Number(modal?.dataset.waitlistStep || 1);
    const errEl = getStepError(modal, step);
    const submitBtn = formEl.querySelector(
      '.waitlist-modal__step[data-step="' + step + '"] [data-waitlist-action="step' + step + '"]',
    );

    if (step === 1) {
      const firstNameInput = document.getElementById('waitlist-modal-first-name');
      const emailInput = document.getElementById('waitlist-modal-email');
      const consentInput = document.getElementById('waitlist-modal-consent');

      // Temporarily ignore step-2 constraints while validating step 1.
      const phoneInput = document.getElementById('waitlist-modal-phone');
      const smsConsentInput = document.getElementById('waitlist-modal-sms-consent');
      const prevPhoneRequired = phoneInput?.required;
      const prevSmsRequired = smsConsentInput?.required;
      if (phoneInput) phoneInput.required = false;
      if (smsConsentInput) smsConsentInput.required = false;

      const step1Valid = formEl.checkValidity();
      if (phoneInput) phoneInput.required = !!prevPhoneRequired;
      if (smsConsentInput) smsConsentInput.required = !!prevSmsRequired;

      if (!step1Valid) {
        formEl.reportValidity?.();
        return;
      }

      const firstNameTrimmed = (firstNameInput?.value || '').trim();
      if (!firstNameTrimmed) {
        if (errEl) {
          errEl.textContent = strings.nameRequired || 'Please enter your name.';
          errEl.hidden = false;
        }
        return;
      }

      if (!consentInput?.checked) {
        if (errEl) {
          errEl.textContent =
            strings.consentRequired || 'Please accept marketing emails to join the waitlist.';
          errEl.hidden = false;
        }
        return;
      }

      setVisible(errEl, false);
      const cfg = getConfigErrors(errEl, strings);
      if (!cfg) return;

      const email = emailInput?.value || '';
      const productTitle = modal?.dataset.waitlistProductTitle || '';
      const payload = buildPayload({
        email: email,
        firstName: firstNameTrimmed,
        listId: cfg.listId,
        productTitle: productTitle,
        appendWaitlistProduct: true,
        includeEmailConsent: true,
      });

      if (submitBtn) submitBtn.disabled = true;

      try {
        const { res, resText, resJson, partialFailure } = await subscribeToKlaviyo(
          payload,
          cfg.companyId,
        );

        if ((res.status === 202 || res.ok) && !partialFailure) {
          modal.dataset.waitlistEmail = email.trim();
          modal.dataset.waitlistFirstName = firstNameTrimmed;
          showStep(modal, 2);
        } else if (res.status === 202 || res.ok) {
          // Email-only payload should not partial-fail on SMS; treat as success → step 2.
          console.warn('[waitlist] Unexpected partial failure on email step', partialFailure);
          modal.dataset.waitlistEmail = email.trim();
          modal.dataset.waitlistFirstName = firstNameTrimmed;
          showStep(modal, 2);
        } else {
          const detail =
            parseKlaviyoErrors(resJson) ||
            resJson?.errors?.[0]?.detail ||
            resJson?.errors?.[0]?.title ||
            '';
          console.error('[waitlist] Klaviyo error', res.status, resText, payload);
          if (errEl) {
            errEl.textContent =
              detail || strings.errorGeneric || 'Something went wrong. Please try again.';
            errEl.hidden = false;
          }
        }
      } catch (err) {
        console.error('[waitlist] Request failed', err);
        if (errEl) {
          errEl.textContent = strings.errorGeneric || 'Something went wrong. Please try again.';
          errEl.hidden = false;
        }
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
      return;
    }

    // Step 2: optional phone + required SMS consent when phone is present
    syncSmsConsentRequired();

    const phoneRaw = document.getElementById('waitlist-modal-phone')?.value || '';
    const smsConsent = document.getElementById('waitlist-modal-sms-consent')?.checked;

    if (!phoneRaw.trim()) {
      if (errEl) {
        errEl.textContent =
          strings.phoneRequiredForSms || 'Enter a mobile number to receive SMS updates.';
        errEl.hidden = false;
      }
      document.getElementById('waitlist-modal-phone')?.focus();
      return;
    }

    const phoneE164 = toE164Phone(phoneRaw);
    if (phoneE164 === null) {
      if (errEl) {
        errEl.textContent =
          strings.phoneInvalid ||
          'Enter a valid mobile number, including country code if outside Australia.';
        errEl.hidden = false;
      }
      return;
    }

    if (!smsConsent) {
      if (errEl) {
        errEl.textContent =
          strings.smsConsentRequired || 'Please accept SMS updates to submit a mobile number.';
        errEl.hidden = false;
      }
      return;
    }

    setVisible(errEl, false);
    const cfg = getConfigErrors(errEl, strings);
    if (!cfg) return;

    const email = modal?.dataset.waitlistEmail || document.getElementById('waitlist-modal-email')?.value || '';
    const firstName =
      modal?.dataset.waitlistFirstName ||
      document.getElementById('waitlist-modal-first-name')?.value ||
      '';
    const productTitle = modal?.dataset.waitlistProductTitle || '';
    // Phone/SMS only — email + waitlist product already saved on step 1.
    const payload = buildPayload({
      email: email,
      firstName: firstName,
      phoneE164: phoneE164,
      listId: cfg.listId,
      productTitle: productTitle,
      appendWaitlistProduct: false,
      includeEmailConsent: false,
    });

    if (submitBtn) submitBtn.disabled = true;

    try {
      const { res, resText, resJson, partialFailure } = await subscribeToKlaviyo(
        payload,
        cfg.companyId,
      );

      if ((res.status === 202 || res.ok) && !partialFailure) {
        showWaitlistSuccessState(modal, strings.success || "You're on the list.");
        setVisible(errEl, false);
      } else if (res.status === 202 || res.ok) {
        console.error('[waitlist] Klaviyo partial failure', partialFailure, payload);
        if (errEl) {
          errEl.textContent =
            strings.errorSmsConfig ||
            'Email was saved, but SMS could not be subscribed. Check Klaviyo SMS is enabled for Australia.';
          errEl.hidden = false;
        }
      } else {
        const detail =
          parseKlaviyoErrors(resJson) ||
          resJson?.errors?.[0]?.detail ||
          resJson?.errors?.[0]?.title ||
          '';
        console.error('[waitlist] Klaviyo error', res.status, resText, payload);
        if (errEl) {
          errEl.textContent =
            detail || strings.errorGeneric || 'Something went wrong. Please try again.';
          errEl.hidden = false;
        }
      }
    } catch (err) {
      console.error('[waitlist] Request failed', err);
      if (errEl) {
        errEl.textContent = strings.errorGeneric || 'Something went wrong. Please try again.';
        errEl.hidden = false;
      }
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });
})();
